import { useState, useEffect, useRef, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';

// Scope requested from Entra so the token is valid for Power BI embed.
// Report.Read.All is the minimum needed to render a report the user already has access to.
const PBI_SCOPES = ['https://analysis.windows.net/powerbi/api/Report.Read.All'];

// Cache the MSAL instance per tenant/client pair so we don't re-initialize on every render.
// The keys change rarely (only when an admin edits Settings), so this is effectively a singleton.
const msalCache = new Map();
function getMsalInstance(tenantId, clientId) {
  const key = `${tenantId}|${clientId}`;
  if (msalCache.has(key)) return msalCache.get(key);
  const instance = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage so tokens don't outlive the browser tab — matches BidBoard's
      // windows-auth-on-every-session feel, and avoids stale tokens across logins.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
  msalCache.set(key, instance);
  return instance;
}

// Acquire a Power BI API token. Tries silent first (cached), falls back to a popup login.
async function acquireToken(msal) {
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) {
    const loginResponse = await msal.loginPopup({ scopes: PBI_SCOPES });
    return loginResponse.accessToken;
  }
  try {
    const silent = await msal.acquireTokenSilent({ scopes: PBI_SCOPES, account: accounts[0] });
    return silent.accessToken;
  } catch (err) {
    // Silent failed (consent/expiry/MFA). Fall back to interactive.
    if (err instanceof InteractionRequiredAuthError) {
      const popup = await msal.acquireTokenPopup({ scopes: PBI_SCOPES });
      return popup.accessToken;
    }
    throw err;
  }
}

// Embeds a Power BI report inside BidBoard.
//
// iframe mode: admin pastes a Secure embed URL in Settings → Reporting. User
// auths against Power BI via their M365 session inside the iframe. No BidBoard-
// side credentials, no Azure AD app registration.
//
// sdk mode: uses @azure/msal-browser to acquire a user token and
// powerbi-client-react to embed with programmatic control over filters, pages,
// theme. Requires a one-time Azure AD app registration and the report's
// workspace + report IDs.
export default function PowerBIReport({ config }) {
  if (!config || config.mode !== 'advanced') return null;

  if (config.embedMethod === 'sdk') {
    return <SdkEmbed config={config} />;
  }
  return <IframeEmbed config={config} />;
}

function IframeEmbed({ config }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [config.embedUrl]);

  if (!config.embedUrl) {
    return (
      <SetupPrompt
        title="Power BI — not configured"
        message="An admin needs to paste a Power BI 'Secure embed' URL into Settings → Reporting. Get it from Power BI Service: open your report → File → Embed report → Website or portal."
      />
    );
  }

  return (
    <div className="powerbi-container">
      {!loaded && !error && <div className="powerbi-loading">Loading Power BI report…</div>}
      {error && (
        <div className="powerbi-error">
          Failed to load the Power BI report. Make sure you are signed into Power BI with your CM Corp account and that the report has been shared with you.
        </div>
      )}
      <iframe
        title="Power BI Report"
        src={config.embedUrl}
        className="powerbi-iframe"
        frameBorder="0"
        allowFullScreen
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

function SdkEmbed({ config }) {
  const { tenantId, clientId, workspaceId, reportId } = config;
  const [token, setToken] = useState(null);
  const [authError, setAuthError] = useState(null);
  const embedRef = useRef(null);

  const hasAllFields = tenantId && clientId && workspaceId && reportId;

  // Acquire an access token on mount. Returned into state so the PowerBIEmbed
  // renders only after we have a valid token.
  useEffect(() => {
    if (!hasAllFields) return;
    let cancelled = false;
    (async () => {
      try {
        const msal = getMsalInstance(tenantId, clientId);
        const t = await acquireToken(msal);
        if (!cancelled) setToken(t);
      } catch (err) {
        if (!cancelled) setAuthError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, clientId, hasAllFields]);

  // When the embed signals the token is about to expire, refresh silently.
  const refreshToken = useCallback(async () => {
    try {
      const msal = getMsalInstance(tenantId, clientId);
      const t = await acquireToken(msal);
      if (embedRef.current) {
        await embedRef.current.setAccessToken(t);
      }
      setToken(t);
    } catch (err) {
      setAuthError(err.message || String(err));
    }
  }, [tenantId, clientId]);

  if (!hasAllFields) {
    return (
      <SetupPrompt
        title="Power BI SDK mode — configuration incomplete"
        message="SDK mode needs the Entra (Azure AD) Tenant ID, Client ID, and the report's Workspace ID + Report ID. An admin can fill these in under Settings → Reporting."
      />
    );
  }

  if (authError) {
    return (
      <div className="powerbi-container">
        <div className="powerbi-error">
          Power BI sign-in failed: {authError}
          <br/><br/>
          Check that the Entra app registration allows this redirect URI, has
          the Power BI Service API delegated permission (<code>Report.Read.All</code>),
          and that admin consent has been granted.
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="powerbi-container">
        <div className="powerbi-loading">Signing in to Power BI…</div>
      </div>
    );
  }

  return (
    <div className="powerbi-container">
      <PowerBIEmbed
        embedConfig={{
          type: 'report',
          id: reportId,
          embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${workspaceId}`,
          accessToken: token,
          tokenType: models.TokenType.Aad,
          settings: {
            // Hide the filter pane so the report looks native to BidBoard.
            // Flip this if you want users to slice within the embedded view.
            filterPaneEnabled: false,
            navContentPaneEnabled: true,
            background: models.BackgroundType.Transparent,
          },
        }}
        eventHandlers={new Map([
          ['loaded', () => {}],
          ['rendered', () => {}],
          ['error', (event) => {
            const detail = event?.detail;
            // eslint-disable-next-line no-console
            console.error('Power BI embed error:', detail);
          }],
          // Fires ~5 min before the token expires. Grab a fresh one.
          ['accessTokenExpired', () => { refreshToken(); }],
        ])}
        cssClassName="powerbi-sdk-embed"
        getEmbeddedComponent={(embeddedReport) => {
          embedRef.current = embeddedReport;
        }}
      />
    </div>
  );
}

function SetupPrompt({ title, message }) {
  return (
    <div className="powerbi-setup">
      <div className="powerbi-setup-icon">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M8 13v4M12 9v8M16 6v11"/>
        </svg>
      </div>
      <div className="powerbi-setup-title">{title}</div>
      <div className="powerbi-setup-message">{message}</div>
    </div>
  );
}
